// ============================================
// Memory System Module (ES6)
// 메모리 패널 UI
// ============================================

import { escapeHtml, showToast, showGlobalLoading, hideGlobalLoading } from './utils.js';
import { authFetch } from './shared/auth-fetch.js';
import { MEMORY_ENDPOINTS } from './shared/api-endpoints.js';
import { getCurrentUser, getCurrentRoomId, getCurrentSessionId, getCurrentTurnCount, getCurrentChatData, getCurrentProfile } from './app/state.js';
import { renderUserNoteTab, clearUserNoteCache } from './chat/user-note.js';
import { ModalManager } from './modal-manager.js';
import { showImageModal } from './image.js';
import { showRenameModal, showSessionModal } from './chat/session-modal.js';
import { restartChatFromSettings, duplicateSessionFromSettings, deleteSessionFromSettings } from './settings/chat-modal.js';
import { openProfileSelector, editProfileFromSelector } from './profile/selector.js';
import { getTtsEnabled, setTtsEnabled, getCurrentVoiceId, setCurrentVoiceId, setupTTSForChat, clearQueue } from './chat/tts/index.js';
import { getVoicePreset } from './config.js';

// 새 메모리 편집 모듈
import { renderMemoryEditTab } from './memory/index.js';
import { resetLayerData } from './memory/state.js';

// ============================================
// State
// ============================================

let currentRenderingTab = null;
let renderRequestId = 0;
let memoryDb = null;

// 탭별 캐시
let memoryCache = {
  state: { data: null, sessionId: null },
  images: { data: null, sessionId: null }
};

// 응답 도착 시 캐시 무효화 (state, memories만 - images/usernote는 별도 처리)
export function invalidateMemoryCache() {
  memoryCache.state.data = null;
  // memories 탭은 memory/state.js의 resetLayerData() 호출
  resetLayerData();
}

// 이미지 캐시 무효화 (이미지 생성 시 호출)
export function invalidateImagesCache() {
  memoryCache.images.data = null;
}

// 세션 전환 시 전체 캐시 무효화
export function invalidateAllCache() {
  memoryCache.state.data = null;
  memoryCache.images.data = null;
  resetLayerData();
  clearUserNoteCache();
}

// ============================================
// Initial Narrative State
// ============================================

function getInitialNarrativeState() {
  return {
    location: { current: '-', previous: null },
    time: { period: '-', description: '-' },
    currentScene: { description: '', mood: '' },
    recentEvents: [],
    presentCharacters: []
  };
}

// ============================================
// Timeline & Profile Sections (탭 위에 항상 표시)
// ============================================

function renderTimelineSection() {
  const container = document.getElementById('timelineSection');
  if (!container) return;

  const chatData = getCurrentChatData();
  const sessionName = escapeHtml(chatData?.sessionName || chatData?.name || '상담');

  container.innerHTML = `
    <div class="memory-session-row">
      <span class="memory-session-label">상담</span>
      <div class="memory-session-name-wrapper">
        <span class="memory-session-name">${sessionName}</span>
        <button class="memory-session-edit-btn" data-action="editSessionName">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>
      </div>
    </div>
    <div class="memory-session-actions">
      <button class="memory-session-action-btn" data-action="newTimeline">새 상담</button>
      <button class="memory-session-action-btn" data-action="duplicateTimeline">복제</button>
      <button class="memory-session-action-btn" data-action="switchTimeline">전환</button>
      <button class="memory-session-action-btn danger" data-action="deleteTimeline">삭제</button>
    </div>
  `;

  // 이벤트 위임 설정
  setupTimelineSectionEvents(container);
}

function renderProfileSection() {
  const container = document.getElementById('profileSection');
  if (!container) return;

  const chatData = getCurrentChatData();
  const profile = chatData?.userProfile || getCurrentProfile();
  const profileName = escapeHtml(profile?.name || '프로필 없음');
  const gender = profile?.gender || '';
  const profileId = profile?.id || null;

  container.innerHTML = `
    <div class="memory-session-row">
      <span class="memory-session-label">프로필</span>
      <div class="memory-session-name-wrapper" data-action="openProfileSelector" style="cursor: pointer;">
        <span class="memory-session-name">${profileName}</span>
        ${gender ? `<span class="timeline-profile-gender">${gender}</span>` : ''}
        ${profileId ? `
          <button class="memory-session-edit-btn" data-action="editCurrentProfile" data-profile-id="${profileId}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
        ` : ''}
        <svg class="timeline-profile-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </div>
    </div>
  `;

  // 이벤트 위임 설정
  setupProfileSectionEvents(container);
}

function setupTimelineSectionEvents(container) {
  container.onclick = (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;

    if (action === 'editSessionName') {
      const chatData = getCurrentChatData();
      const sessionId = getCurrentSessionId();
      const roomId = getCurrentRoomId();
      const sessionName = chatData?.sessionName || '세션';
      showRenameModal(sessionId, sessionName, roomId);
    } else if (action === 'newTimeline') {
      restartChatFromSettings();
    } else if (action === 'duplicateTimeline') {
      duplicateSessionFromSettings();
    } else if (action === 'switchTimeline') {
      const roomId = getCurrentRoomId();
      const chatData = getCurrentChatData();
      if (roomId) {
        showSessionModal(roomId, chatData?.title || '상담 목록');
      }
    } else if (action === 'deleteTimeline') {
      deleteSessionFromSettings();
    }
  };
}

function setupProfileSectionEvents(container) {
  container.onclick = (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;

    if (action === 'editCurrentProfile') {
      e.stopPropagation();
      const profileId = actionEl.dataset.profileId;
      if (profileId) {
        editProfileFromSelector(profileId);
      }
    } else if (action === 'openProfileSelector') {
      openProfileSelector();
    }
  };
}

// ============================================
// TTS Toggle Section
// ============================================

function renderTtsSection() {
  const container = document.getElementById('ttsSection');
  if (!container) return;

  const chatData = getCurrentChatData();
  const voiceId = chatData?.character?.voiceId;

  // 음성이 설정되지 않은 캐릭터는 TTS 섹션 숨김
  if (!voiceId) {
    container.style.display = 'none';
    return;
  }

  container.style.display = 'block';

  // 현재 캐릭터 voiceId 설정
  setCurrentVoiceId(voiceId);

  const preset = getVoicePreset(voiceId);
  const voiceName = preset?.name || '기본 음성';
  const isEnabled = getTtsEnabled();

  // DOM 직접 생성 (XSS 방지)
  container.textContent = '';

  const row = document.createElement('div');
  row.className = 'tts-toggle-row';

  const info = document.createElement('div');
  info.className = 'tts-toggle-info';

  // 아이콘
  const icon = document.createElement('div');
  icon.className = 'tts-toggle-icon';
  icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>';

  const textDiv = document.createElement('div');
  textDiv.className = 'tts-toggle-text';

  const label = document.createElement('span');
  label.className = 'tts-toggle-label';
  label.textContent = '음성 읽기';

  const voice = document.createElement('span');
  voice.className = 'tts-toggle-voice';
  voice.textContent = voiceName;

  textDiv.appendChild(label);
  textDiv.appendChild(voice);
  info.appendChild(icon);
  info.appendChild(textDiv);

  const toggleLabel = document.createElement('label');
  toggleLabel.className = 'tts-toggle-switch';

  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.dataset.action = 'toggleTts';
  checkbox.checked = isEnabled;

  const slider = document.createElement('span');
  slider.className = 'tts-toggle-slider';

  toggleLabel.appendChild(checkbox);
  toggleLabel.appendChild(slider);

  row.appendChild(info);
  row.appendChild(toggleLabel);
  container.appendChild(row);

  // 이벤트 설정
  setupTtsSectionEvents(container);
}

function setupTtsSectionEvents(container) {
  container.onclick = (e) => {
    const checkbox = e.target.closest('[data-action="toggleTts"]');
    if (!checkbox) return;

    const newState = checkbox.checked;
    setTtsEnabled(newState);

    if (!newState) {
      // TTS 끄면 재생 중인 큐 클리어
      clearQueue();
    }

    showToast(newState ? '음성 읽기가 켜졌습니다' : '음성 읽기가 꺼졌습니다');
  };
}

// TTS 섹션 새로고침 (export)
export function refreshTtsSection() {
  renderTtsSection();
}

// ============================================
// Memory Panel UI
// ============================================

export function toggleMemoryPanel() {
  const panel = document.getElementById('memoryPanel');
  if (!panel) return;

  const isOpening = !panel.classList.contains('active');
  panel.classList.toggle('active');

  const overlay = document.getElementById('mobileOverlay');
  if (overlay && window.innerWidth <= 768) {
    overlay.classList.toggle('show', panel.classList.contains('active'));
  }

  if (isOpening) {
    // 상담/프로필/TTS 섹션 렌더링
    renderTimelineSection();
    renderProfileSection();
    renderTtsSection();

    ModalManager.register('memoryPanel', closeMemoryPanel, 'panel');
    ModalManager.push('memoryPanel');
  } else {
    ModalManager.remove('memoryPanel', true);
    ModalManager.unregister('memoryPanel');
  }
}

export function closeMemoryPanel() {
  const panel = document.getElementById('memoryPanel');
  if (panel && panel.classList.contains('active')) {
    panel.classList.remove('active');
    const overlay = document.getElementById('mobileOverlay');
    if (overlay) overlay.classList.remove('show');
  }

  ModalManager.remove('memoryPanel', true);
  ModalManager.unregister('memoryPanel');
}

// 현재 활성 탭 가져오기
export function getCurrentMemoryTab() {
  const activeTab = document.querySelector('.memory-tab.active');
  return activeTab?.dataset.tab || currentRenderingTab || 'state';
}

// 현재 탭 유지하며 새로고침
export async function refreshMemoryPanel() {
  const currentTab = getCurrentMemoryTab();
  await renderMemoryPanel(currentTab);
}

export async function renderMemoryPanel(tab) {
  const content = document.getElementById('memoryContent');

  currentRenderingTab = tab;
  const thisRequestId = ++renderRequestId;

  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();
  const user = getCurrentUser();

  // 세션 구조만 지원 (레거시 chats 컬렉션 제거됨)
  if (!roomId || !sessionId) {
    content.innerHTML = '<div style="color: var(--text-secondary); text-align: center; padding: 20px;">세션을 선택하세요</div>';
    return;
  }

  if (tab === 'usernote') {
    await renderUserNoteTab();
    return;
  }

  if (tab === 'images') {
    await renderImagesTab(content, user.uid, roomId, sessionId);
    return;
  }

  // 새 메모리 편집 탭 (5레이어 시스템)
  if (tab === 'memories') {
    await renderMemoryEditTab(user.uid, roomId, sessionId);
    return;
  }

  // state 탭 캐시 확인
  let data = {};
  let useCache = false;

  if (memoryCache.state.data && memoryCache.state.sessionId === sessionId) {
    data = memoryCache.state.data;
    useCache = true;
  }

  if (!useCache) {
    // 로딩 표시 (정적 HTML, XSS 위험 없음)
    content.innerHTML = '<div style="display: flex; justify-content: center; padding: 40px;"><div class="spinner"></div></div>';

    if (!memoryDb) memoryDb = firebase.firestore();

    try {
    // 세션 구조만 지원 (레거시 chats 컬렉션 제거됨)
    if (!roomId || !sessionId) {
      content.innerHTML = '<div style="color: var(--text-secondary); text-align: center; padding: 20px;">세션을 선택하세요</div>';
      return;
    }

    const roomRef = memoryDb.collection('users').doc(user.uid)
      .collection('chatRooms').doc(roomId);
    const chatRef = roomRef.collection('sessions').doc(sessionId);

    const [roomDoc, chatDoc, memoriesSnap, eventsSnap] = await Promise.all([
      roomRef.get(),
      chatRef.get(),
      chatRef.collection('memories').orderBy('createdAt', 'desc').limit(100).get(),
      chatRef.collection('events').orderBy('turn', 'desc').limit(10).get()
    ]);

    if (thisRequestId !== renderRequestId) {
      return;
    }

    if (!chatDoc.exists) {
      content.innerHTML = '<div style="color: var(--text-secondary); text-align: center; padding: 20px;">로드 실패</div>';
      return;
    }

    const chatData = chatDoc.data();
    const roomData = roomDoc.exists ? roomDoc.data() : {};

    const recentEvents = eventsSnap.docs.map(doc => doc.data());

    // character와 characters는 chatRoom 문서에 저장됨
    const character = roomData.character || null;
    const characters = roomData.characters || [];
    const mode = roomData.mode || 'character';
    const world = chatData.world || roomData.world || null;

    let narrativeState = chatData.narrativeState || getInitialNarrativeState();
    // 최근 이벤트 반영
    if (recentEvents.length > 0) {
      narrativeState.recentEvents = recentEvents.map(e => ({ title: e.title, description: e.description }));
    }

    data = {
      narrativeState: narrativeState,
      character: character,
      mode: mode,
      world: world,
      characters: characters,
      memories: { core: [], episodic: [], semantic: [] },
      recentEvents: recentEvents
    };

    memoriesSnap.docs.forEach(doc => {
      const mem = { id: doc.id, ...doc.data() };
      if (mem.type === 'core' && data.memories.core) {
        data.memories.core.push(mem);
      } else if (mem.type === 'episodic' && data.memories.episodic) {
        data.memories.episodic.push(mem);
      } else if (mem.type === 'semantic' && data.memories.semantic) {
        data.memories.semantic.push(mem);
      }
    });

    // 캐시에 저장
    memoryCache.state.data = data;
    memoryCache.state.sessionId = sessionId;
    } catch (e) {
      console.error('메모리 로드 오류:', e);
      content.innerHTML = '<div style="color: var(--text-secondary); text-align: center; padding: 20px;">로드 실패</div>';
      return;
    }
  }

  if (thisRequestId !== renderRequestId) {
    return;
  }

  const state = data.narrativeState || getInitialNarrativeState();
  const character = data.character;
  const mode = data.mode || 'character';
  const world = data.world;
  const characters = data.characters || [];
  const memories = data.memories || { core: [], episodic: [], semantic: [] };

  if (tab === 'state') {
    let characterSection = '';

    if (mode === 'story' && world) {
      characterSection = `
        <div class="memory-section core">
          <div class="memory-section-title">세계관</div>
          <div class="memory-item">
            <strong>${escapeHtml(world.title)}</strong>
            <div style="font-size: 12px; color: var(--text-secondary); margin-top: 4px;">
              ${escapeHtml(world.genre)} · ${escapeHtml(world.setting || '')}
            </div>
          </div>
        </div>

        <div class="memory-section">
          <div class="memory-section-title">등장인물</div>
          ${characters.map(c => `
            <div class="char-status-card ${c.imageUrl ? 'has-image' : ''}">
              ${c.imageUrl ? `
                <div class="char-card-image" data-action="showImage" data-url="${c.imageUrl}" data-name="${escapeHtml(c.name)}">
                  <div class="img-loading-wrapper" style="width:100%;height:100%"><div class="spinner"></div><img src="${c.imageUrl}" alt="${escapeHtml(c.name)}" style="width:100%;height:100%;object-fit:cover" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')"></div>
                </div>
              ` : ''}
              <div class="char-status-header">
                <div class="char-status-avatar">${c.imageUrl ? `<img src="${c.imageUrl}" alt="${escapeHtml(c.name)}" data-action="showImage" data-url="${c.imageUrl}" data-name="${escapeHtml(c.name)}">` : (c.gender === '여성' ? '👩' : '👨')}</div>
                <div>
                  <div class="char-status-name">${escapeHtml(c.name)}</div>
                  <div class="char-status-role">${escapeHtml(c.role)}</div>
                </div>
              </div>
              <div class="char-status-info">
                <div class="char-status-row">
                  <span class="char-status-label">위치</span>
                  <span class="char-status-value">${escapeHtml(c.currentLocation || state.location.current)}</span>
                </div>
                <div class="char-status-row">
                  <span class="char-status-label">기분</span>
                  <span class="char-status-value">${escapeHtml(c.currentMood || '평온')}</span>
                </div>
                <div class="char-status-row">
                  <span class="char-status-label">관계</span>
                  <span>${escapeHtml(c.relationship || c.role)}</span>
                </div>
              </div>
              <div class="char-memory-list">
                <div class="char-memory-item">성격: ${escapeHtml(c.personality)}</div>
              </div>
            </div>
          `).join('')}
        </div>
      `;
    } else if (character) {
      characterSection = `
        <div class="memory-section core">
          ${character.imageUrl ? `
            <div class="memory-char-hero" data-action="showImage" data-url="${character.imageUrl}" data-name="${escapeHtml(character.name)}">
              <div class="img-loading-wrapper" style="width:100%;height:100%"><div class="spinner"></div><img src="${character.imageUrl}" alt="${escapeHtml(character.name)}" style="width:100%;height:100%;object-fit:cover;border-radius:12px" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')"></div>
              <div class="memory-char-hero-overlay">
                <span class="memory-char-hero-name">${escapeHtml(character.name)}</span>
              </div>
            </div>
          ` : `<div class="memory-section-title" style="margin-bottom: 12px;">${escapeHtml(character.name)}</div>`}
          <div class="char-info-grid">
            <div class="char-info-item">
              <span class="char-info-label">나이</span>
              <span class="char-info-value">${escapeHtml(String(character.age))}세</span>
            </div>
            <div class="char-info-item">
              <span class="char-info-label">성별</span>
              <span class="char-info-value">${escapeHtml(character.gender)}</span>
            </div>
            <div class="char-info-item full">
              <span class="char-info-label">관계</span>
              <span class="char-info-value">${escapeHtml(character.relationship || '-')}</span>
            </div>
          </div>
        </div>
      `;
    }

    let questSection = '';
    if (mode === 'story' && state.currentScene?.mainQuest) {
      questSection = `
        <div class="memory-section core">
          <div class="memory-section-title">현재 목표</div>
          <div class="memory-item">${escapeHtml(state.currentScene.mainQuest)}</div>
        </div>
      `;
    }

    const recentEvents = state.recentEvents || [];
    const eventsSection = recentEvents.length > 0 ? `
      <div class="memory-section narrative">
        <div class="memory-section-title">최근 사건</div>
        ${recentEvents.slice(0, 5).map(e => `
          <div class="memory-item" style="margin-bottom: 8px;">
            <strong>${escapeHtml(e.title) || '이벤트'}</strong>
            <div style="font-size: 12px; color: var(--text-secondary); margin-top: 2px;">${escapeHtml(e.description) || ''}</div>
          </div>
        `).join('')}
      </div>
    ` : '';

    content.innerHTML = `
      <div class="memory-section narrative">
        <div class="memory-section-title">현재 진행</div>
        <div class="state-item">
          <span class="state-key">턴</span>
          <span class="state-value">${getCurrentTurnCount() || 0}</span>
        </div>
        <div class="state-item">
          <span class="state-key">시간</span>
          <span class="state-value">${escapeHtml(state.time.description || state.time.period || '-')}</span>
        </div>
        <div class="state-item">
          <span class="state-key">장소</span>
          <span class="state-value">${escapeHtml(state.location.current || '-')}</span>
        </div>
      </div>

      <div class="memory-section narrative">
        <div class="memory-section-title">현재 상황</div>
        <div class="memory-item">${escapeHtml(state.currentScene.description || '특별한 상황 없음')}</div>
      </div>

      ${eventsSection}
      ${characterSection}
      ${questSection}
    `;

    // 이벤트 위임 설정
    setupMemoryContentEvents(content);
  } else {
    const storyDays = new Set();
    [...memories.core, ...memories.episodic, ...memories.semantic].forEach(mem => {
      if (mem.storyContext?.storyDay) storyDays.add(mem.storyContext.storyDay);
    });

    const totalCount = memories.core.length + memories.episodic.length + memories.semantic.length;
    const sortedDays = Array.from(storyDays).sort((a, b) => b - a);

    const renderMemoryItem = (m, showType = false) => {
      const ctx = m.storyContext || {};
      const hasContext = ctx.storyDay || ctx.time || ctx.location;
      const importanceBar = m.importance ? `
        <div class="memory-importance-bar">
          <div class="memory-importance-fill" style="width: ${m.importance * 100}%"></div>
        </div>` : '';

      return `
        <div class="memory-item-enhanced" data-id="${m.id}" data-day="${ctx.storyDay || 0}">
          <div class="memory-item-content">${escapeHtml(m.content)}</div>
          ${hasContext ? `
            <div class="memory-item-context">
              ${ctx.storyDay ? `<span class="mem-ctx-tag mem-ctx-day">${ctx.storyDay}일차</span>` : ''}
              ${ctx.time ? `<span class="mem-ctx-tag mem-ctx-time">${escapeHtml(ctx.time)}</span>` : ''}
              ${ctx.location ? `<span class="mem-ctx-tag mem-ctx-location">${escapeHtml(ctx.location)}</span>` : ''}
            </div>
          ` : ''}
          <div class="memory-item-footer">
            ${showType ? `<span class="mem-type-badge mem-type-${m.type}">${m.type}</span>` : ''}
            ${importanceBar}
            ${m.turnNumber ? `<span class="mem-turn">턴 ${m.turnNumber}</span>` : ''}
          </div>
        </div>
      `;
    };

    content.innerHTML = `
      <div class="memory-overview">
        <div class="memory-stat-bar">
          <span class="mem-stat">총 ${totalCount}개</span>
          <span class="mem-stat core">${memories.core.length}</span>
          <span class="mem-stat episodic">${memories.episodic.length}</span>
          <span class="mem-stat semantic">${memories.semantic.length}</span>
        </div>
        ${sortedDays.length > 1 ? `
          <div class="memory-day-filter">
            <button class="mem-day-btn active" data-day="all">전체</button>
            ${sortedDays.slice(0, 5).map(d => `<button class="mem-day-btn" data-day="${d}">${d}일차</button>`).join('')}
          </div>
        ` : ''}
      </div>

      <div class="memory-section core">
        <div class="memory-section-header">
          <span class="memory-section-title">핵심 기억</span>
          <span class="memory-section-count">${memories.core.length}</span>
        </div>
        <div class="memory-section-desc">관계 변화, 고백, 비밀 등 중요한 순간</div>
        ${memories.core.length > 0
          ? memories.core.map(m => renderMemoryItem(m)).join('')
          : '<div class="memory-empty">아직 핵심 기억이 없습니다</div>'
        }
      </div>

      <div class="memory-section episodic">
        <div class="memory-section-header">
          <span class="memory-section-title">에피소드 기억</span>
          <span class="memory-section-count">${memories.episodic.length}</span>
        </div>
        <div class="memory-section-desc">함께한 경험과 대화</div>
        ${memories.episodic.length > 0
          ? memories.episodic.slice(0, 15).map(m => renderMemoryItem(m)).join('')
          : '<div class="memory-empty">아직 에피소드 기억이 없습니다</div>'
        }
        ${memories.episodic.length > 15 ? `
          <button class="memory-show-more" data-action="showAllMemories" data-type="episodic">
            +${memories.episodic.length - 15}개 더 보기
          </button>
        ` : ''}
      </div>

      <div class="memory-section semantic">
        <div class="memory-section-header">
          <span class="memory-section-title">학습된 정보</span>
          <span class="memory-section-count">${memories.semantic.length}</span>
        </div>
        <div class="memory-section-desc">점술가가 알게 된 정보</div>
        ${memories.semantic.length > 0
          ? memories.semantic.map(m => renderMemoryItem(m)).join('')
          : '<div class="memory-empty">아직 학습된 정보가 없습니다</div>'
        }
      </div>
    `;

    // 이벤트 위임 설정
    setupMemoryContentEvents(content);

    content.querySelectorAll('.mem-day-btn').forEach(btn => {
      btn.onclick = () => {
        content.querySelectorAll('.mem-day-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const day = btn.dataset.day;
        content.querySelectorAll('.memory-item-enhanced').forEach(item => {
          if (day === 'all' || item.dataset.day === day || item.dataset.day === '0') {
            item.style.display = '';
          } else {
            item.style.display = 'none';
          }
        });
      };
    });
  }
}

function setupMemoryContentEvents(content) {
  content.onclick = (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;

    if (action === 'showImage') {
      const url = actionEl.dataset.url;
      const name = actionEl.dataset.name || '이미지';
      showImageModal(url, name);
    } else if (action === 'showAllMemories') {
      const type = actionEl.dataset.type;
      console.log('[Memory] showAllMemories 호출:', type);
    } else if (action === 'editSummary') {
      openSummaryEditModal();
    }
  };
}

// ============================================
// 요약 편집 모달
// ============================================

function openSummaryEditModal() {
  const summaryEl = document.querySelector('.summary-content');
  const currentSummary = summaryEl ? summaryEl.textContent : '';

  const modalId = 'summary-edit-modal';
  const html = `
    <div class="modal-overlay" id="${modalId}" style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 10000;">
      <div class="modal-content" style="background: var(--bg-secondary); border-radius: 12px; width: 90%; max-width: 600px; max-height: 80vh; display: flex; flex-direction: column;">
        <div class="modal-header" style="padding: 16px; border-bottom: 1px solid var(--border-color); display: flex; justify-content: space-between; align-items: center;">
          <h3 style="margin: 0; font-size: 16px;">요약 편집</h3>
          <button data-action="closeModal" style="background: none; border: none; cursor: pointer; padding: 4px;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="modal-body" style="padding: 16px; flex: 1; overflow: auto;">
          <textarea id="summaryEditTextarea" style="width: 100%; height: 300px; padding: 12px; border: 1px solid var(--border-color); border-radius: 8px; background: var(--bg-primary); color: var(--text-primary); font-size: 14px; line-height: 1.6; resize: vertical;">${escapeHtml(currentSummary)}</textarea>
        </div>
        <div class="modal-footer" style="padding: 16px; border-top: 1px solid var(--border-color); display: flex; justify-content: flex-end; gap: 8px;">
          <button data-action="closeModal" style="padding: 8px 16px; border: 1px solid var(--border-color); border-radius: 6px; background: var(--bg-tertiary); cursor: pointer;">취소</button>
          <button data-action="saveSummary" style="padding: 8px 16px; border: none; border-radius: 6px; background: var(--accent-color); color: white; cursor: pointer;">저장</button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', html);
  const modal = document.getElementById(modalId);

  modal.onclick = async (e) => {
    const action = e.target.closest('[data-action]')?.dataset.action;
    if (action === 'closeModal' || e.target === modal) {
      modal.remove();
    } else if (action === 'saveSummary') {
      const textarea = document.getElementById('summaryEditTextarea');
      const newSummary = textarea.value.trim();
      await saveSummary(newSummary);
      modal.remove();
    }
  };

  // textarea 포커스
  document.getElementById('summaryEditTextarea')?.focus();
}

async function saveSummary(summary) {
  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();

  if (!roomId || !sessionId) {
    showToast('세션 정보가 없습니다', 'error');
    return;
  }

  showGlobalLoading('요약 저장 중...');

  try {
    const response = await authFetch(MEMORY_ENDPOINTS.updateSummary, {
      body: { roomId, sessionId, summary }
    });

    hideGlobalLoading();

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '요약 저장 실패');
    }

    // 캐시 무효화 및 UI 업데이트
    memoryCache.state.data = null;
    showToast('요약이 저장되었습니다');

    // state 탭 다시 렌더링
    const content = document.getElementById('memoryContent');
    if (content) {
      renderStateTab(content, getCurrentUser()?.uid, roomId, sessionId, 'state');
    }
  } catch (error) {
    hideGlobalLoading();
    console.error('[saveSummary] 오류:', error);
    showToast(error.message || '요약 저장 실패', 'error');
  }
}

// ============================================
// Images Tab - 생성된 장면 이미지 갤러리
// ============================================

async function renderImagesTab(content, userId, roomId, sessionId) {
  // 세션 구조만 지원
  if (!roomId || !sessionId) {
    content.innerHTML = '<div style="color: var(--text-secondary); text-align: center; padding: 20px;">세션을 선택하세요</div>';
    return;
  }

  // 캐시 확인
  if (memoryCache.images.data && memoryCache.images.sessionId === sessionId) {
    renderImagesContent(content, memoryCache.images.data);
    return;
  }

  content.innerHTML = '<div style="display: flex; justify-content: center; padding: 40px;"><div class="spinner"></div></div>';

  if (!memoryDb) memoryDb = firebase.firestore();

  try {
    const chatRef = memoryDb.collection('users').doc(userId)
      .collection('chatRooms').doc(roomId)
      .collection('sessions').doc(sessionId);

    const sceneImagesSnap = await chatRef.collection('sceneImages')
      .orderBy('createdAt', 'desc')
      .get();

    const images = sceneImagesSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // 캐시에 저장
    memoryCache.images.data = images;
    memoryCache.images.sessionId = sessionId;

    renderImagesContent(content, images);

  } catch (e) {
    console.error('이미지 로드 오류:', e);
    content.innerHTML = '<div style="color: var(--text-secondary); text-align: center; padding: 20px;">이미지 로드 실패</div>';
  }
}

function renderImagesContent(content, images) {
  if (images.length === 0) {
    content.innerHTML = `
      <div class="images-empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="width: 48px; height: 48px; opacity: 0.4; margin-bottom: 12px;">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21 15 16 10 5 21"/>
        </svg>
        <div style="color: var(--text-secondary); font-size: 14px;">생성된 이미지가 없습니다</div>
        <div style="color: var(--text-muted); font-size: 12px; margin-top: 4px;">채팅에서 이미지 버튼을 눌러 생성하세요</div>
      </div>
    `;
    return;
  }

  content.innerHTML = `
    <div class="images-gallery-header">
      <span class="images-count">${images.length}개의 이미지</span>
    </div>
    <div class="images-gallery-grid">
      ${images.map(img => `
        <div class="gallery-image-item" data-action="showImage" data-url="${img.imageUrl}" data-name="생성된 이미지">
          <div class="img-loading-wrapper">
            <div class="spinner"></div>
            <img src="${img.imageUrl}" alt="생성된 이미지" loading="lazy" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')">
          </div>
        </div>
      `).join('')}
    </div>
  `;

  // 이벤트 위임 설정
  setupMemoryContentEvents(content);
}

// ============================================
// User Note Tab - chat/user-note.js에서 관리
// ============================================
// renderUserNoteTab 함수는 window.renderUserNoteTab으로 호출됨

// ============================================
// window 노출 제거됨 (2026-01-06)
// 모든 호출이 ES6 import로 처리됨
// ============================================
